import { DateGrouping, QueryAggregate } from '@powerportalspro/core';
export { DateGrouping, JoinOperator, QueryAggregate } from '@powerportalspro/core';
import { forwardRef, useRef, useCallback, useEffect, useImperativeHandle } from 'react';
import { Chart, registerables } from 'chart.js';
import { FunnelController, TrapezoidElement } from 'chartjs-chart-funnel';
import { jsx } from 'react/jsx-runtime';

// src/types.ts
var ChartType = {
  /** Bar chart (`'bar'`). */
  Bar: "bar",
  /** Line chart (`'line'`). */
  Line: "line",
  /** Pie chart (`'pie'`). */
  Pie: "pie",
  /** Doughnut chart (`'doughnut'`). */
  Doughnut: "doughnut",
  /** Polar area chart (`'polarArea'`). */
  PolarArea: "polarArea",
  /** Radar chart (`'radar'`). */
  Radar: "radar",
  /** Bubble chart (`'bubble'`). */
  Bubble: "bubble",
  /** Scatter chart (`'scatter'`). */
  Scatter: "scatter",
  /** Funnel chart (via the `chartjs-chart-funnel` plugin, type `'funnel'`). */
  Funnel: "funnel"
};
var ChartLegendPosition = {
  Top: "top",
  Bottom: "bottom",
  Left: "left",
  Right: "right",
  /** Legend is not rendered. */
  Hidden: "hidden"
};
var ChartIndexAxis = {
  X: "x",
  Y: "y"
};
var ChartOrientation = {
  Vertical: "vertical",
  Horizontal: "horizontal"
};
function toIndexAxis(orientation) {
  return orientation === ChartOrientation.Horizontal ? ChartIndexAxis.Y : ChartIndexAxis.X;
}
var registered = false;
function ensureRegistered() {
  if (registered) return;
  Chart.register(...registerables, FunnelController, TrapezoidElement);
  registered = true;
}
var RADIAL_TYPES = /* @__PURE__ */ new Set(["pie", "doughnut", "polararea", "radar", "funnel"]);
function trimmed(value) {
  if (value === null || value === void 0) return void 0;
  const t = value.trim();
  return t.length > 0 ? t : void 0;
}
function numberFormatter(format, currency) {
  const trimmedFormat = format?.trim();
  if (!trimmedFormat) return null;
  const locale = typeof document !== "undefined" && document.documentElement.lang || void 0;
  try {
    const standard = /^([CcDdEeFfGgNnPp])(\d{0,2})$/.exec(trimmedFormat);
    if (standard) {
      const digits = standard[2] === "" ? void 0 : Number(standard[2]);
      const fixed = (places) => ({ minimumFractionDigits: places, maximumFractionDigits: places });
      let options;
      switch (standard[1].toUpperCase()) {
        case "N":
          options = fixed(digits ?? 2);
          break;
        case "F":
          options = { ...fixed(digits ?? 2), useGrouping: false };
          break;
        case "D":
          options = { maximumFractionDigits: 0, minimumIntegerDigits: Math.max(1, digits ?? 1), useGrouping: false };
          break;
        case "P":
          options = { style: "percent", ...fixed(digits ?? 2) };
          break;
        case "E":
          options = { notation: "scientific", ...fixed(digits ?? 6) };
          break;
        case "C":
          options = currency ? { style: "currency", currency, ...fixed(digits ?? 2) } : fixed(digits ?? 2);
          break;
        default:
          options = digits ? { maximumSignificantDigits: digits } : {};
          break;
      }
      const formatter2 = new Intl.NumberFormat(locale, options);
      return (value) => formatter2.format(value);
    }
    const custom = /^([^0#.,]*)([0#,]*(?:\.[0#]*)?)([^0#]*)$/.exec(trimmedFormat);
    if (!custom || !/[0#]/.test(custom[2])) return null;
    const unquote = (text) => text.replace(/['"]/g, "");
    const prefix = unquote(custom[1]);
    const suffix = unquote(custom[3]);
    const [integer = "", fraction = ""] = custom[2].split(".");
    const percent = (prefix + suffix).includes("%");
    const formatter = new Intl.NumberFormat(locale, {
      useGrouping: integer.includes(","),
      minimumIntegerDigits: Math.max(1, (integer.match(/0/g) ?? []).length),
      minimumFractionDigits: (fraction.match(/0/g) ?? []).length,
      maximumFractionDigits: fraction.length
    });
    return (value) => `${prefix}${formatter.format(percent ? value * 100 : value)}${suffix}`;
  } catch {
    return null;
  }
}
function formatValue(raw, prefix, suffix, format) {
  const num = typeof raw === "number" ? raw : Number(raw);
  const formatted = Number.isFinite(num) ? format ? format(num) : num.toLocaleString() : String(raw);
  return `${prefix ?? ""}${formatted}${suffix ?? ""}`;
}
function valueTick(prefix, suffix, format) {
  return function(value) {
    return formatValue(value, prefix, suffix, format);
  };
}
function categoryTick(prefix, suffix) {
  return function(value, index) {
    const scale = this;
    const label = typeof scale?.getLabelForValue === "function" ? scale.getLabelForValue(value) : scale?.chart?.data?.labels?.[index] ?? value;
    if (Array.isArray(label)) {
      return label.map(
        (line, i) => `${i === 0 ? prefix ?? "" : ""}${line}${i === label.length - 1 ? suffix ?? "" : ""}`
      );
    }
    return `${prefix ?? ""}${label}${suffix ?? ""}`;
  };
}
function tooltipValue(parsed, valueAxis) {
  if (typeof parsed === "number") return parsed;
  const point = parsed;
  for (const key of [valueAxis, "r"]) {
    const value = point?.[key];
    if (typeof value === "number") return value;
  }
  return null;
}
function buildChartOptions(type, theme, onClick) {
  const text = trimmed(theme?.textColor);
  const grid = trimmed(theme?.gridColor);
  const yPrefix = trimmed(theme?.yAxisPrefix);
  const ySuffix = trimmed(theme?.yAxisSuffix);
  const xPrefix = trimmed(theme?.xAxisPrefix);
  const xSuffix = trimmed(theme?.xAxisSuffix);
  const hasYFormat = yPrefix !== void 0 || ySuffix !== void 0;
  const hasXFormat = xPrefix !== void 0 || xSuffix !== void 0;
  const valueFormat = numberFormatter(theme?.valueFormat, theme?.valueCurrency);
  const animationEnabled = theme?.enableAnimation !== false;
  const titleText = trimmed(theme?.title);
  const legendPos = trimmed(theme?.legendPosition);
  const stacked = theme?.stacked === true;
  const indexAxis = trimmed(theme?.indexAxis) ?? (type.toLowerCase() === "funnel" ? "y" : void 0);
  const options = {
    responsive: true,
    maintainAspectRatio: false,
    ...indexAxis ? { indexAxis } : {}
  };
  options.plugins = options.plugins ?? {};
  if (legendPos === "hidden") {
    options.plugins.legend = { display: false };
  } else {
    options.plugins.legend = {
      ...legendPos ? { position: legendPos } : {},
      ...text ? { labels: { color: text } } : {}
    };
  }
  if (titleText) {
    options.plugins.title = {
      display: true,
      text: titleText,
      ...text ? { color: text } : {}
    };
  } else if (text) {
    options.plugins.title = { color: text };
  }
  const horizontal = indexAxis === "y" && !RADIAL_TYPES.has(type.toLowerCase());
  const valuePrefix = horizontal ? xPrefix : yPrefix;
  const valueSuffix = horizontal ? xSuffix : ySuffix;
  if (valuePrefix !== void 0 || valueSuffix !== void 0 || valueFormat) {
    const valueAxis = indexAxis === "y" ? "x" : "y";
    options.plugins.tooltip = {
      callbacks: {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        label: (ctx) => {
          const value = tooltipValue(ctx.parsed, valueAxis);
          if (value === null) return ctx.dataset.label ?? "";
          const label = ctx.dataset.label ? `${ctx.dataset.label}: ` : "";
          return `${label}${formatValue(value, valuePrefix, valueSuffix, valueFormat)}`;
        }
      }
    };
  }
  if (text) {
    options.color = text;
  }
  if (!animationEnabled) {
    options.animation = false;
  }
  const isRadial = RADIAL_TYPES.has(type.toLowerCase());
  if (!isRadial) {
    const x = {};
    const y = {};
    if (text) {
      x.ticks = { color: text };
      y.ticks = { color: text };
    }
    if (grid) {
      x.grid = { color: grid };
      x.border = { color: grid };
      y.grid = { color: grid };
      y.border = { color: grid };
    }
    if (hasYFormat || valueFormat && !horizontal) {
      y.ticks = y.ticks ?? {};
      y.ticks.callback = horizontal ? categoryTick(yPrefix, ySuffix) : valueTick(yPrefix, ySuffix, valueFormat);
    }
    if (hasXFormat || valueFormat && horizontal) {
      x.ticks = x.ticks ?? {};
      x.ticks.callback = horizontal ? valueTick(xPrefix, xSuffix, valueFormat) : categoryTick(xPrefix, xSuffix);
    }
    if (stacked) {
      x.stacked = true;
      y.stacked = true;
    }
    options.scales = { x, y };
  }
  if (onClick) {
    options.onClick = (_event, elements, chart) => {
      if (!elements || elements.length === 0) return;
      const first = elements[0];
      const datasetIndex = first.datasetIndex;
      const dataIndex = first.index;
      const dataset = chart.data.datasets[datasetIndex];
      const labels = chart.data.labels;
      const rawValue = dataset?.data?.[dataIndex];
      const value = typeof rawValue === "number" ? rawValue : rawValue && typeof rawValue === "object" && "y" in rawValue ? Number(rawValue.y) : Number(rawValue ?? 0);
      onClick({
        datasetIndex,
        dataIndex,
        label: labels?.[dataIndex] != null ? String(labels[dataIndex]) : "",
        value: Number.isFinite(value) ? value : 0,
        datasetLabel: typeof dataset?.label === "string" ? dataset.label : ""
      });
    };
  }
  return options;
}
function projectDatasets(_type, datasets) {
  return datasets.map((d) => {
    const points = d.data ?? [];
    const flatData = points.map((p) => p.value);
    const hasBg = points.some((p) => p.backgroundColor !== void 0);
    const hasBorder = points.some((p) => p.borderColor !== void 0);
    const bgColors = hasBg ? points.map((p) => p.backgroundColor ?? d.backgroundColor) : void 0;
    const borderColors = hasBorder ? points.map((p) => p.borderColor ?? d.borderColor) : void 0;
    return {
      ...d,
      data: flatData,
      backgroundColor: bgColors ?? d.backgroundColor,
      borderColor: borderColors ?? d.borderColor
    };
  });
}
function updateChartDatasets(chart, type, datasets) {
  chart.data.datasets = projectDatasets(type, datasets);
  chart.update("none");
}
function createChart(canvas, type, labels, datasets, theme, onClick) {
  ensureRegistered();
  const lingering = Chart.getChart(canvas);
  if (lingering) {
    lingering.destroy();
  }
  const config = {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    type,
    data: {
      labels,
      datasets: projectDatasets(type, datasets)
    },
    options: buildChartOptions(type, theme, onClick)
  };
  return new Chart(canvas, config);
}
function destroyChart(chart) {
  if (chart) {
    chart.destroy();
  }
}
function exportChartAsImage(chart) {
  if (!chart) return null;
  return chart.toBase64Image("image/png", 1);
}
function getContainerStyle(width, height) {
  return {
    position: "relative",
    minWidth: 0,
    maxWidth: "100%",
    width: width ?? "100%",
    ...height !== void 0 ? { height } : {}
  };
}
function ChartImpl(props, ref) {
  const {
    type = ChartType.Bar,
    labels,
    datasets,
    width,
    height = "400px",
    theme,
    onElementClick
  } = props;
  const canvasRef = useRef(null);
  const chartRef = useRef(null);
  const latestPropsRef = useRef({ datasets, onElementClick });
  latestPropsRef.current = { datasets, onElementClick };
  const handleClick = useCallback((payload) => {
    const { datasets: currentDatasets, onElementClick: currentHandler } = latestPropsRef.current;
    if (!currentHandler) return;
    const dataset = payload.datasetIndex >= 0 && payload.datasetIndex < currentDatasets.length ? currentDatasets[payload.datasetIndex] : void 0;
    const points = dataset?.data ?? [];
    const dataPoint = payload.dataIndex >= 0 && payload.dataIndex < points.length ? points[payload.dataIndex] : void 0;
    currentHandler({
      datasetIndex: payload.datasetIndex,
      dataIndex: payload.dataIndex,
      label: payload.label,
      value: payload.value,
      datasetLabel: payload.datasetLabel,
      ...dataPoint !== void 0 ? { dataPoint } : {}
    });
  }, []);
  const lastDrawn = useRef(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const inputsChanged = !lastDrawn.current || lastDrawn.current.type !== type || lastDrawn.current.labels !== labels || lastDrawn.current.datasets !== datasets || lastDrawn.current.theme !== theme;
    if (!inputsChanged && chartRef.current) {
      return;
    }
    const chart = chartRef.current;
    if (chart && lastDrawn.current && lastDrawn.current.type === type && lastDrawn.current.theme === theme && lastDrawn.current.labels === labels && isStyleOnlyDatasetChange(lastDrawn.current.datasets, datasets)) {
      updateChartDatasets(chart, type, datasets);
      lastDrawn.current = { type, labels, datasets, theme };
      return;
    }
    destroyChart(chartRef.current);
    chartRef.current = createChart(canvas, type, labels, datasets, theme, handleClick);
    lastDrawn.current = { type, labels, datasets, theme };
  }, [type, labels, datasets, theme, handleClick]);
  useEffect(() => {
    return () => {
      destroyChart(chartRef.current);
      chartRef.current = null;
    };
  }, []);
  useImperativeHandle(
    ref,
    () => ({
      exportAsImage: () => exportChartAsImage(chartRef.current)
    }),
    []
  );
  return /* @__PURE__ */ jsx("div", { className: "ppp-chart", style: getContainerStyle(width, height), children: /* @__PURE__ */ jsx(
    "canvas",
    {
      ref: canvasRef,
      style: { display: "block", maxWidth: "100%", maxHeight: "100%" }
    }
  ) });
}
var Chart2 = forwardRef(ChartImpl);
function isStyleOnlyDatasetChange(prev, next) {
  if (prev.length !== next.length) return false;
  for (let i = 0; i < prev.length; i++) {
    const p = prev[i];
    const n = next[i];
    if (p.label !== n.label) return false;
    const pData = p.data ?? [];
    const nData = n.data ?? [];
    if (pData.length !== nData.length) return false;
    for (let j = 0; j < pData.length; j++) {
      const pv = pData[j].value;
      const nv = nData[j].value;
      if (pv !== nv) return false;
    }
  }
  return true;
}

// src/dataverse-chart-data-source.ts
var DataverseChartDataSource = class {
  fetchXml;
  labelColumn = "";
  valueColumn = "";
  seriesColumn;
  singleSeriesLabel = "";
  constructor(options = {}) {
    Object.assign(this, options);
  }
  /**
   * Builds a {@link ChartDataRequest} from this instance's properties
   * and POSTs it via `client.loadChartAsync`. Subclasses inject the
   * input mode via {@link configureRequest}.
   */
  async loadAsync(client, signal) {
    const request = {
      labelColumn: this.labelColumn,
      valueColumn: this.valueColumn
    };
    if (this.seriesColumn !== void 0) request.seriesColumn = this.seriesColumn;
    if (this.singleSeriesLabel) request.singleSeriesLabel = this.singleSeriesLabel;
    this.configureRequest(request);
    const response = await client.loadChartAsync(request, signal);
    return response.data ?? { labels: [], datasets: [] };
  }
  /**
   * Sets the input mode (FetchXml / ViewId / Aggregate) on the request.
   * Base implementation sets `fetchXml`; subclasses override to use
   * ViewId or Aggregate config instead.
   */
  configureRequest(request) {
    if (this.fetchXml !== void 0) request.fetchXml = this.fetchXml;
  }
};

// src/view-dataverse-chart-data-source.ts
var ViewDataverseChartDataSource = class extends DataverseChartDataSource {
  viewId;
  constructor(options = {}) {
    super(options);
    if (options.viewId !== void 0) {
      this.viewId = options.viewId;
    }
  }
  configureRequest(request) {
    if (!this.viewId) {
      throw new Error('"viewId" must be set before calling loadAsync.');
    }
    request.viewId = this.viewId;
  }
};
var AggregateDataverseChartDataSource = class extends DataverseChartDataSource {
  tableName;
  groupByDateGrouping = DateGrouping.None;
  groupByLinkedEntity;
  groupByRangeSize;
  groupByIncludeBlank = true;
  groupByFormat;
  seriesDateGrouping = DateGrouping.None;
  seriesLinkedEntity;
  seriesRangeSize;
  seriesIncludeBlank = true;
  seriesFormat;
  aggregate = QueryAggregate.Sum;
  viewId;
  filterXml;
  top;
  splitOverRecordLimit = false;
  splitSliceCount;
  splitDateColumn;
  maximumRangedValues;
  constructor(options) {
    super(options);
    this.tableName = options.tableName;
    this.labelColumn = options.groupByColumn;
    this.valueColumn = options.aggregateColumn;
    if (options.groupByDateGrouping !== void 0) this.groupByDateGrouping = options.groupByDateGrouping;
    if (options.groupByLinkedEntity !== void 0) this.groupByLinkedEntity = options.groupByLinkedEntity;
    if (options.groupByRangeSize !== void 0) this.groupByRangeSize = options.groupByRangeSize;
    if (options.groupByIncludeBlank !== void 0) this.groupByIncludeBlank = options.groupByIncludeBlank;
    if (options.groupByFormat !== void 0) this.groupByFormat = options.groupByFormat;
    if (options.seriesDateGrouping !== void 0) this.seriesDateGrouping = options.seriesDateGrouping;
    if (options.seriesLinkedEntity !== void 0) this.seriesLinkedEntity = options.seriesLinkedEntity;
    if (options.seriesRangeSize !== void 0) this.seriesRangeSize = options.seriesRangeSize;
    if (options.seriesIncludeBlank !== void 0) this.seriesIncludeBlank = options.seriesIncludeBlank;
    if (options.seriesFormat !== void 0) this.seriesFormat = options.seriesFormat;
    if (options.aggregate !== void 0) this.aggregate = options.aggregate;
    if (options.viewId !== void 0) this.viewId = options.viewId;
    if (options.filterXml !== void 0) this.filterXml = options.filterXml;
    if (options.top !== void 0) this.top = options.top;
    if (options.splitOverRecordLimit !== void 0) this.splitOverRecordLimit = options.splitOverRecordLimit;
    if (options.splitSliceCount !== void 0) this.splitSliceCount = options.splitSliceCount;
    if (options.splitDateColumn !== void 0) this.splitDateColumn = options.splitDateColumn;
    if (options.maximumRangedValues !== void 0) this.maximumRangedValues = options.maximumRangedValues;
  }
  /** Caller-friendly alias for `labelColumn` (kept in sync via getter/setter). */
  get groupByColumn() {
    return this.labelColumn;
  }
  set groupByColumn(value) {
    this.labelColumn = value;
  }
  /** Caller-friendly alias for `valueColumn`. */
  get aggregateColumn() {
    return this.valueColumn;
  }
  set aggregateColumn(value) {
    this.valueColumn = value;
  }
  configureRequest(request) {
    const groupBy = {
      column: this.groupByColumn,
      dateGrouping: this.groupByDateGrouping
    };
    if (this.groupByLinkedEntity) groupBy.linkedEntity = this.groupByLinkedEntity;
    if (this.groupByRangeSize !== void 0) groupBy.rangeSize = this.groupByRangeSize;
    if (!this.groupByIncludeBlank) groupBy.includeBlank = false;
    if (this.groupByFormat !== void 0) groupBy.format = this.groupByFormat;
    let series;
    if (this.seriesColumn !== void 0 || this.seriesDateGrouping !== DateGrouping.None || this.seriesLinkedEntity !== void 0) {
      series = {
        column: this.seriesColumn ?? "",
        dateGrouping: this.seriesDateGrouping
      };
      if (this.seriesLinkedEntity) series.linkedEntity = this.seriesLinkedEntity;
      if (this.seriesRangeSize !== void 0) series.rangeSize = this.seriesRangeSize;
      if (!this.seriesIncludeBlank) series.includeBlank = false;
      if (this.seriesFormat !== void 0) series.format = this.seriesFormat;
    }
    const config = {
      tableName: this.tableName,
      groupBy,
      aggregateColumn: this.aggregateColumn,
      aggregate: this.aggregate
    };
    if (series !== void 0) config.series = series;
    if (this.viewId !== void 0) config.viewFilterId = this.viewId;
    if (this.filterXml !== void 0) config.filterXml = this.filterXml;
    if (this.top !== void 0) config.top = this.top;
    if (this.splitOverRecordLimit) {
      config.splitOverRecordLimit = true;
      if (this.splitSliceCount !== void 0) config.splitSliceCount = this.splitSliceCount;
      if (this.splitDateColumn !== void 0) config.splitDateColumn = this.splitDateColumn;
    }
    if (this.maximumRangedValues !== void 0) config.maximumRangedValues = this.maximumRangedValues;
    request.aggregate = config;
  }
};

// src/index.ts
var VERSION = "0.1.0";

export { AggregateDataverseChartDataSource, Chart2 as Chart, ChartIndexAxis, ChartLegendPosition, ChartOrientation, ChartType, DataverseChartDataSource, VERSION, ViewDataverseChartDataSource, toIndexAxis };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map